/**
 * English UI catalogue for namespace 'templates' (W2-05 easing-templates-raw): the motion templates dialog
 * (DESIGN.md §4.13) and its commands. The failure reasons and notes `applyTemplate` produces ('templates.reason.*',
 * 'templates.note.*') live in the pure-data catalogue next to it, src/core/tools/templates.messages.ts.
 */

import { catalogOf, defineMessages } from '../../i18n/define';

export const m = defineMessages('templates', {
  title: 'Motion templates',
  target: 'Line {line}, starting at {beat}',
  chooserLabel: 'Template',
  'name.fadeIn': 'Fade in',
  'name.fadeOut': 'Fade out',
  'name.slideIn': 'Slide in',
  'name.slideOut': 'Slide out',
  'name.moveTo': 'Move here',
  'name.spin': 'Spin',
  'name.swing': 'Swing',
  'name.pulse': 'Pulse',
  'name.shake': 'Shake',
  'name.flash': 'Flash colour',
  'name.orbitParent': 'Orbit',
  'about.fadeIn': 'Opacity rises from a start value to a target, then stays.',
  'about.fadeOut': 'Opacity falls to a target, then stays.',
  'about.slideIn': 'The line comes in from a side (or a point) and ends where it already is.',
  'about.slideOut': 'The line leaves towards a side (or a point) and stays there.',
  'about.moveTo': 'The line moves to a point and stays there.',
  'about.spin': 'The line turns by whole or partial turns and keeps the new angle.',
  'about.swing': 'The line swings back and forth around its angle, as an exact sine wave, and comes back.',
  'about.pulse': 'The line grows on each pulse and eases back to its size.',
  'about.shake': 'The line jitters around its position (the same pattern for the same seed) and comes back.',
  'about.flash': 'The colour jumps to the flash colour and eases back.',
  'about.orbitParent': 'The line circles its parent line (or the screen centre) at its current distance.',
  start: 'Start',
  duration: 'Duration',
  curve: 'Curve',
  curveChange: 'Change curve',
  curveFixed: 'This template shapes its own motion.',
  from: 'From',
  to: 'To',
  opacityFrom: 'Start opacity',
  opacityTo: 'Target opacity',
  side: 'Side',
  sideIn: 'Come in from',
  sideOut: 'Leave towards',
  'side.left': 'Left',
  'side.right': 'Right',
  'side.top': 'Top',
  'side.bottom': 'Bottom',
  'side.point': 'A point',
  distance: 'Distance',
  pointX: 'Point X',
  pointY: 'Point Y',
  usePosition: "Use the line's position",
  turns: 'Turns',
  direction: 'Direction',
  clockwise: 'Clockwise',
  counterClockwise: 'Counter-clockwise',
  amplitude: 'Amount',
  swings: 'Swings',
  settle: 'Settle down towards the end',
  amount: 'Size at the peak',
  pulses: 'Pulses',
  axes: 'Axes',
  'axes.both': 'Both',
  'axes.x': 'X only',
  'axes.y': 'Y only',
  shakes: 'Moves',
  seed: 'Pattern',
  newSeed: 'New pattern',
  color: 'Flash colour',
  radius: 'Radius',
  radiusHelp: "0 keeps the line's current distance from its parent.",
  asNewLayer: 'Apply as a new layer',
  asNewLayerHelp: 'The motion is added on top of the existing keyframes, which stay untouched.',
  asNewLayerNo: 'Size and colour have no layers.',
  previewNote: 'The preview loops the span. Nothing changes until you apply.',
  previewOff: 'Loop the span in the preview',
  apply: 'Apply',
  cancel: 'Cancel',
  applied: '{name} added: {count, plural, one {# keyframe} other {# keyframes}}.',
  failed: 'This template cannot be applied: {reason}',
  noLine: 'Choose a line first.',
  txApply: 'Template: {name}',
  cmdOpen: 'Motion templates...',
  cmdTemplate: 'Template: {name}...',
  'cmd.fadeIn': 'Template: Fade in...',
  'cmd.fadeOut': 'Template: Fade out...',
  'cmd.slideIn': 'Template: Slide in...',
  'cmd.slideOut': 'Template: Slide out...',
  'cmd.moveTo': 'Template: Move here...',
  'cmd.spin': 'Template: Spin...',
  'cmd.swing': 'Template: Swing...',
  'cmd.pulse': 'Template: Pulse...',
  'cmd.shake': 'Template: Shake...',
  'cmd.flash': 'Template: Flash colour...',
  'cmd.orbitParent': 'Template: Orbit...',
});

export default catalogOf(m);
